import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Products() {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState("");
  
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        // Error handled silently
      }
    };
    fetchCategories();
  }, []);
  
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = selectedCategory && selectedCategory !== 'all' 
          ? `${API}/products?category=${selectedCategory}`
          : `${API}/products`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);
  
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
  };
  
  // Filtrer les produits par recherche
  const filteredProducts = products.filter(product => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      product.name_fr.toLowerCase().includes(query) ||
      product.name_en.toLowerCase().includes(query) ||
      product.sku.toLowerCase().includes(query) ||
      product.description_fr.toLowerCase().includes(query)
    );
  });
  
  const getCategoryName = (cat) => {
    return language === 'fr' ? cat.name_fr : cat.name_en;
  };
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24" data-testid="products-page">
      <SEO 
        title="AfriOne B&D"
        description="AfriOne B&D"
        ogTitle="AfriOne B&D"
        ogDescription="AfriOne B&D"
        keywords="produits africains, poisson africain, poulet, manioc, plantain, gombo, épices africaines"
        canonical="https://afrionebd.com/products"
      />
      {/* Header */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">Collection</span>
            <h1 className="text-3xl lg:text-5xl font-bold font-heading text-text_primary mt-2 tracking-tight" data-testid="products-title">
              {t('allProducts')}
            </h1>
          </motion.div>
        </div>
      </section>
      
      {/* Categories Filter + Search Bar */}
      <section className="sticky top-16 lg:top-20 z-40 bg-white/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'fr' ? "Rechercher un produit..." : "Search for a product..."}
                className="w-full px-6 py-3 pl-12 rounded-full border-2 border-gray-200 focus:border-primary focus:outline-none transition-all"
              />
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {/* Categories */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === cat.id
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-secondary/50 text-text_secondary hover:bg-secondary'
                }`}
                data-testid={`category-filter-${cat.id}`}
              >
                {getCategoryName(cat)}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Products Grid */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                  <div className="aspect-square bg-gray-200 rounded-xl mb-4" />
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-text_secondary text-lg">
                {searchQuery 
                  ? (language === 'fr' ? 'Aucun produit trouvé pour votre recherche' : 'No products found for your search')
                  : 'Aucun produit trouvé'
                }
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="mt-4 px-6 py-2 bg-primary text-white rounded-full hover:bg-primary_hover"
                >
                  {language === 'fr' ? 'Effacer la recherche' : 'Clear search'}
                </button>
              )}
            </div>
          ) : (
            <>
              {searchQuery && (
                <div className="mb-6 text-center">
                  <p className="text-text_secondary">
                    {language === 'fr' 
                      ? `${filteredProducts.length} produit${filteredProducts.length > 1 ? 's' : ''} trouvé${filteredProducts.length > 1 ? 's' : ''}`
                      : `${filteredProducts.length} product${filteredProducts.length > 1 ? 's' : ''} found`
                    }
                  </p>
                </div>
              )}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                data-testid="products-grid"
              >
                {filteredProducts.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </motion.div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
